import { Fragment, useMemo } from 'react';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDownUp, ChevronDown, ChevronRight, GripVertical, Plus, Trash2 } from 'lucide-react';
import {
  capitalizeFirstWord,
  formatTimelineSpan,
  outOfOrderEntryIds,
  type TimelineEntry,
  type TimelineSection,
} from '@peles/shared';
import { InlineEditText } from '../common/InlineEditText';
import { TimelineEntryRow } from './TimelineEntryRow';
import { TimelineEntryForm } from './TimelineEntryForm';
import type { EntryFormState } from './entryForm';

/** Which entry form is open, and where. */
export type EntryDraft =
  | { kind: 'add'; sectionId: string; afterEntryId: string | null }
  | { kind: 'edit'; entryId: string };

interface TimelineSectionCardProps {
  section: TimelineSection;
  expanded: boolean;
  onToggle: (sectionId: string) => void;
  readOnly?: boolean;
  draft: EntryDraft | null;
  form: EntryFormState;
  onChangeForm: (form: EntryFormState) => void;
  saving: boolean;
  onSaveDraft: () => void;
  onCancelDraft: () => void;
  onStartAdd: (sectionId: string, afterEntryId?: string) => void;
  onStartEdit: (entry: TimelineEntry) => void;
  onDeleteEntry: (entry: TimelineEntry) => void;
  onRename: (sectionId: string, name: string) => Promise<unknown>;
  onDelete: (section: TimelineSection) => void;
  onSortByTime: (section: TimelineSection) => void;
}

/**
 * A section of the run of show (Ceremony, Reception…) as a card: a header
 * with its name (click to rename), how many entries and the hours they
 * cover, and a warning with a one-press fix when its times run backwards;
 * then its entries on a time rail, each sortable, and the entry form inline.
 */
export function TimelineSectionCard(props: TimelineSectionCardProps) {
  const { section, expanded, onToggle, readOnly = false, draft } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    disabled: readOnly,
  });

  const outOfOrder = useMemo(() => new Set(outOfOrderEntryIds(section.entries)), [section.entries]);
  const span = formatTimelineSpan(section.entries);
  const count = section.entries.length;
  const addingHere = draft?.kind === 'add' && draft.sectionId === section.id;

  const form = (testId: string, submitLabel: string) => (
    <TimelineEntryForm
      form={props.form}
      onChange={props.onChangeForm}
      onSave={props.onSaveDraft}
      onCancel={props.onCancelDraft}
      saving={props.saving}
      submitLabel={submitLabel}
      testId={testId}
    />
  );

  return (
    <section
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 10 : undefined,
      }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm"
      aria-label={section.name}
      data-testid={`section-${section.id}`}
    >
      <header className="flex items-center gap-1 px-2 sm:px-3 py-2">
        {!readOnly && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="flex items-center justify-center min-h-11 w-7 sm:min-h-0 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none"
            aria-label={`Drag section ${section.name}`}
            data-testid={`drag-section-${section.id}`}
          >
            <GripVertical size={16} />
          </button>
        )}
        <button
          type="button"
          onClick={() => onToggle(section.id)}
          className="flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 sm:p-1 rounded text-gray-500 hover:bg-gray-100"
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${section.name}` : `Expand ${section.name}`}
          data-testid={`toggle-section-${section.id}`}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-1">
          {readOnly ? (
            <h3 className="font-semibold text-gray-900">{capitalizeFirstWord(section.name)}</h3>
          ) : (
            <InlineEditText
              value={capitalizeFirstWord(section.name)}
              onSave={(name) => props.onRename(section.id, name)}
              label="Section name"
              className="font-semibold text-gray-900"
            />
          )}
          <span className="text-xs text-gray-500" data-testid={`section-meta-${section.id}`}>
            {count === 1 ? '1 entry' : `${count} entries`}
            {span && ` · ${span}`}
          </span>
        </div>

        {!readOnly && outOfOrder.size > 0 && (
          <button
            type="button"
            onClick={() => props.onSortByTime(section)}
            className="flex items-center gap-1 min-h-11 sm:min-h-0 px-2 py-1 rounded-md text-xs font-medium text-amber-800 bg-amber-50 hover:bg-amber-100"
            title="Some times are earlier than the entry above them — put the section in time order"
            data-testid={`sort-section-${section.id}`}
          >
            <ArrowDownUp size={13} aria-hidden />
            <span className="hidden sm:inline">Sort by time</span>
            <span className="sr-only sm:hidden">Sort by time</span>
          </button>
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={() => props.onDelete(section)}
            className="flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 sm:p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
            aria-label={`Delete section ${section.name}`}
            data-testid={`delete-section-${section.id}`}
          >
            <Trash2 size={15} />
          </button>
        )}
      </header>

      {expanded && (
        <div className="border-t border-gray-100 px-2 sm:px-3 py-2">
          <SortableContext
            items={section.entries.map((e) => e.id)}
            strategy={verticalListSortingStrategy}
          >
            {count === 0 && !addingHere && (
              <p className="py-3 text-center text-sm text-gray-400">
                {readOnly ? 'Nothing in this section yet.' : 'No entries yet — add the first one.'}
              </p>
            )}
            <div>
              {section.entries.map((entry) => (
                <Fragment key={entry.id}>
                  {draft?.kind === 'edit' && draft.entryId === entry.id ? (
                    form(`edit-entry-form-${entry.id}`, 'Save')
                  ) : (
                    <TimelineEntryRow
                      entry={entry}
                      outOfOrder={outOfOrder.has(entry.id)}
                      readOnly={readOnly}
                      onEdit={props.onStartEdit}
                      onDelete={props.onDeleteEntry}
                      onAddBelow={(id) => props.onStartAdd(section.id, id)}
                    />
                  )}
                  {addingHere &&
                    draft.afterEntryId === entry.id &&
                    form(`add-entry-form-after-${entry.id}`, 'Add')}
                </Fragment>
              ))}
            </div>
          </SortableContext>

          {!readOnly &&
            (addingHere && !draft.afterEntryId ? (
              form('add-entry-form', 'Add')
            ) : (
              <button
                type="button"
                onClick={() => props.onStartAdd(section.id)}
                className="mt-1 flex items-center gap-1.5 min-h-11 sm:min-h-0 px-2 py-1.5 rounded-md text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                data-testid={`add-entry-button-${section.id}`}
              >
                <Plus size={14} />
                Add entry
              </button>
            ))}
        </div>
      )}
    </section>
  );
}
